import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import type { ApiError } from "@/lib/api/unwrap";
import type { components } from "@/lib/api/schema.gen";

import {
  patchProfile,
  qKeys,
  useCharacteristics,
  useCompanySizes,
  useCreateProfile,
  useSectors,
} from "../api";
import { ProfileFields } from "../components/ProfileFields";
import {
  RespondentSection,
  type RespondentHandle,
  type RespondentValues,
} from "../components/RespondentSection";
import { ErrorBlock, LoadingBlock } from "../components/QueryState";
import {
  PROFILE_FIELD_KEYS,
  REQUIRED_FIELDS,
  splitFieldErrors,
} from "../profile-utils";
import {
  EMPTY_PROFILE,
  type EnterpriseProfileDetail,
  type ProfileFormValues,
} from "../types";

const ENTERPRISE_TYPES = [
  "self_employed",
  "private",
  "cooperative",
  "not_for_profit",
  "economical_development",
  "municipality",
  "other",
] as const;

/**
 * First-run gate: a brand-new (self-mode) user has no enterprise profile, so we
 * collect it once before the diagnostic. Sectors are part of this step because
 * they determine the unavoidable issues. This is the only time the full profile
 * form blocks the path — afterwards the profile is a quiet side-door.
 */
export function CreateProfileGate({ onCreated }: { onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const sectors = useSectors();
  const characteristics = useCharacteristics();
  const companySizes = useCompanySizes();
  const createProfile = useCreateProfile();

  const [values, setValues] = useState<ProfileFormValues>(EMPTY_PROFILE);
  const [errors, setErrors] = useState<
    Partial<Record<keyof ProfileFormValues, string>>
  >({});
  // The respondent's name is required on Django's identification form, in the
  // same POST that creates the profile. Here the section owns its own state and
  // autosaves to /me, so without mirroring it the asterisks on those fields
  // were decoration: "Creer" went through with no name at all.
  const [respondent, setRespondent] = useState<RespondentValues>({
    first_name: "",
    last_name: "",
    position: "",
  });
  const [respondentErrors, setRespondentErrors] = useState<
    Partial<Record<keyof RespondentValues, string>>
  >({});
  const respondentRef = useRef<RespondentHandle>(null);

  const patch = (p: Partial<ProfileFormValues>) =>
    setValues((prev) => ({ ...prev, ...p }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Partial<Record<keyof ProfileFormValues, string>> = {};
    for (const field of REQUIRED_FIELDS) {
      if (!String(values[field]).trim()) {
        nextErrors[field] = t("questionnaire.identification.required");
      }
    }
    // At least one sector, as Django's identification form demands. Checked
    // here rather than through `REQUIRED_FIELDS`, which is also what
    // `missingRequired()` feeds to `MissingFieldsGate`: adding it there would
    // route every existing sectorless profile into a gate that renders a plain
    // text input for an array field and PATCHes a string into it. This rule is
    // for creation only.
    if (values.sector_ids.length === 0) {
      nextErrors.sector_ids = t("questionnaire.identification.required");
    }
    setErrors(nextErrors);

    const nextRespondent: Partial<Record<keyof RespondentValues, string>> = {};
    for (const field of ["first_name", "last_name"] as const) {
      if (!respondent[field].trim()) {
        nextRespondent[field] = t("questionnaire.identification.required");
      }
    }
    setRespondentErrors(nextRespondent);

    if (
      Object.keys(nextErrors).length > 0 ||
      Object.keys(nextRespondent).length > 0
    )
      return;

    // The respondent's name lives in a section that autosaves on a debounce and
    // is unmounted the moment this succeeds. Requiring the field without
    // flushing it first would let a name typed just before the click pass
    // validation and never be written — the section is the last thing above the
    // button, so that is the ordinary sequence, not an edge case.
    if (!(await respondentRef.current?.flush())) return;

    createProfile.mutate(
      {
        name: values.name,
        enterprise_type:
          values.enterprise_type as EnterpriseProfileDetail["enterprise_type"],
        address: values.address,
        city: values.city,
        zip_code: values.zip_code,
        phone_number: values.phone_number,
        website: values.website || undefined,
        email: values.email || undefined,
        description: values.description || undefined,
        sectors: values.sector_ids,
        characteristics: values.characteristic_ids,
        company_size: values.company_size_id || null,
      },
      {
        onSuccess: (created) => {
          toast.success(t("questionnaire.identification.created"));
          onCreated(created.id);
        },
        onError: (err) => {
          const apiError = err as unknown as ApiError;
          const { rendered, unrendered } = splitFieldErrors(
            apiError?.fieldErrors,
            PROFILE_FIELD_KEYS,
          );
          setErrors(rendered as Partial<Record<keyof ProfileFormValues, string>>);
          // A field error names the field and the reason (a taken enterprise
          // name, say), so a toast on top of it only adds noise. Anything this
          // form cannot show still needs saying, or the save fails in silence.
          if (unrendered.length > 0) {
            toast.error(unrendered.join(" "));
          } else if (Object.keys(rendered).length === 0) {
            toast.error(apiError?.detail ?? t("questionnaire.saveError"));
          }
        },
      },
    );
  };

  if (sectors.isLoading || characteristics.isLoading || companySizes.isLoading) {
    return <LoadingBlock />;
  }
  if (sectors.error) return <ErrorBlock error={sectors.error} />;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {t("questionnaire.identification.createTitle")}
        </h1>
        <p className="mt-1 text-muted-foreground">
          {t("questionnaire.identification.createSubtitle")}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <ProfileFields
          values={values}
          onChange={patch}
          sectors={sectors.data ?? []}
          characteristics={characteristics.data ?? []}
          companySizes={companySizes.data ?? []}
          errors={errors}
        />
        <RespondentSection
          ref={respondentRef}
          onChange={setRespondent}
          errors={respondentErrors}
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={createProfile.isPending}>
            {createProfile.isPending
              ? t("questionnaire.identification.creating")
              : t("questionnaire.identification.create")}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * Missing-fields gate: the profile exists but a required field is blank (e.g.
 * an invited member whose profile was pre-created without a phone number). We
 * ask *only* for what's missing, once, then continue — never the whole form.
 */
export function MissingFieldsGate({
  profileId,
  missing,
  onReady,
}: {
  profileId: string;
  missing: (keyof ProfileFormValues)[];
  onReady: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(missing.map((f) => [f, ""])),
  );
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  // Errors the server names a field for — a name whose showcase slug is already
  // taken, say. `errors` above only records "this was left blank", so without
  // these a 400 that says exactly what is wrong would show as the generic save
  // toast, which is the failure this gate's own name field can now hit.
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const set = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, boolean> = {};
    for (const field of missing) {
      if (!values[field]?.trim()) nextErrors[field] = true;
    }
    setErrors(nextErrors);
    setServerErrors({});
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    try {
      // Patch only the fields we collected — never touch sectors/characteristics.
      await patchProfile(
        profileId,
        values as components["schemas"]["PatchedEnterpriseProfileDetail"],
      );
      await qc.invalidateQueries({ queryKey: qKeys.profile(profileId) });
      onReady();
    } catch (err) {
      const apiError = err as ApiError;
      // This gate renders only the fields it asked for, so anything else the
      // server names — non_field_errors, a field it did not collect — has to be
      // toasted rather than dropped on the floor.
      const { rendered, unrendered } = splitFieldErrors(
        apiError?.fieldErrors,
        new Set<string>(missing),
      );
      setServerErrors(rendered);
      if (unrendered.length > 0) {
        toast.error(unrendered.join(" "));
      } else if (Object.keys(rendered).length === 0) {
        toast.error(apiError?.detail ?? t("questionnaire.saveError"));
      }
    } finally {
      setSaving(false);
    }
  };

  const labelFor = (field: keyof ProfileFormValues) =>
    ({
      name: t("questionnaire.identification.name"),
      enterprise_type: t("questionnaire.identification.enterpriseType"),
      address: t("questionnaire.identification.address"),
      city: t("questionnaire.identification.city"),
      zip_code: t("questionnaire.identification.zipCode"),
      phone_number: t("questionnaire.identification.phone"),
    })[field as string] ?? field;

  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle>{t("questionnaire.gate.fieldsTitle")}</CardTitle>
          <CardDescription>
            {t("questionnaire.gate.fieldsSubtitle", { count: missing.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {missing.map((field) => {
              const id = `gate-${field}`;
              const serverError = serverErrors[field];
              const invalid = errors[field] || serverError !== undefined;
              return (
                <div key={field} className="space-y-1.5">
                  <Label htmlFor={id}>
                    {labelFor(field)}
                    <span aria-hidden className="text-destructive"> *</span>
                  </Label>
                  {field === "enterprise_type" ? (
                    <select
                      id={id}
                      value={values[field]}
                      aria-invalid={invalid}
                      onChange={(e) => set(field, e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-[invalid=true]:border-destructive"
                    >
                      <option value="">
                        {t("questionnaire.identification.selectPlaceholder")}
                      </option>
                      {ENTERPRISE_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {t(`questionnaire.enterpriseTypes.${type}`)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id={id}
                      value={values[field]}
                      aria-invalid={invalid}
                      onChange={(e) => set(field, e.target.value)}
                    />
                  )}
                  {serverError && (
                    <p className="text-sm font-medium text-destructive">
                      {serverError}
                    </p>
                  )}
                </div>
              );
            })}
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button type="submit" disabled={saving}>
                {saving ? t("questionnaire.gate.saving") : t("questionnaire.gate.start")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
